import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function HomePage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "BrainCommerce",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "BrainCommerce designs the future of work by integrating humans, AI, and robotics into a single workforce architecture.",
    "sameAs": [
      "https://www.linkedin.com/company/braincommerce",
      "https://twitter.com/braincommerce"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+82-2-3456-7890",
      "contactType": "Customer Service",
      "email": "contact@braincommerce.com",
      "areaServed": "KR",
      "availableLanguage": ["Korean", "English"]
    }
  };

  return (
    <>
      <SEO
        title="BrainCommerce - We Design the Future of Work | Hybrid Workforce Solutions"
        description="BrainCommerce designs the future of work by integrating humans, AI, and robotics into a single workforce architecture. Experience the integrated ecosystem of Manpower and BC Robotics."
        keywords="hybrid workforce, workforce architecture, AI workforce, robotic automation, Manpower, BC Robotics, workforce solutions, digital transformation"
        canonical={siteUrl}
        ogTitle="BrainCommerce - We Design the Future of Work"
        ogDescription="Integrating Humans, AI, and Robotics into a single workforce architecture"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center">
          <div className="absolute inset-0 flex flex-col md:flex-row">
            {/* Left Text Zone */}
            <div className="w-full md:w-2/5 flex items-center px-4 md:px-8 lg:pl-32 pt-20 md:pt-0">
              <h1 className="text-[26px] sm:text-5xl md:text-6xl lg:text-[84px] font-extralight leading-[1.1] text-[#1a1a1a] tracking-tight pb-8 md:pb-12 lg:pb-16">
                {t('home.hero.title')}
              </h1>
            </div>
            
            {/* Right Image Zone */}
            <div className="w-full md:w-3/5 relative flex-1 md:flex-none">
              <img 
                src="https://public.readdy.ai/ai/img_res/afab9813f4e00da6cd417d4f7a84ddf1.jpg"
                alt="Human and Robot Collaboration"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
            </div>
          </div>

          {/* Bottom Data Bar */}
          <div className="absolute bottom-0 left-0 right-0 h-16 md:h-20 bg-black/85 flex items-center justify-around px-4 md:px-8 lg:px-32">
            <div className="flex items-center gap-2 md:gap-3">
              <i className="ri-user-line text-white text-lg md:text-2xl"></i>
              <span className="text-white text-xs md:text-sm font-light tracking-wide">{t('home.hero.human')}</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <i className="ri-brain-line text-white text-lg md:text-2xl"></i>
              <span className="text-white text-xs md:text-sm font-light tracking-wide">{t('home.hero.ai')}</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <i className="ri-robot-line text-white text-lg md:text-2xl"></i>
              <span className="text-white text-xs md:text-sm font-light tracking-wide">{t('home.hero.robotics')}</span>
            </div>
          </div>
        </section>

        {/* Philosophy Statement */}
        <section className="py-16 md:py-32 lg:py-48 px-4 md:px-8">
          <div className="max-w-[720px] mx-auto text-center">
            <h2 className="text-[28px] sm:text-2xl md:text-2xl lg:text-[32px] font-extralight text-[#1a1a1a] mb-6 md:mb-8 leading-tight tracking-tight">
              {t('home.philosophy.headline')}
            </h2>
            <p className="text-base md:text-lg text-[#4a4a4a] leading-relaxed">
              {t('home.philosophy.body')}
            </p>
          </div>
        </section>

        {/* Business Overview Grid */}
        <section className="py-12 md:py-24 px-4 md:px-8 lg:px-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Manpower Card */}
            <div className="bg-white rounded-lg p-6 md:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.06)] min-h-[360px] md:h-[420px] flex flex-col">
              <span className="text-xs uppercase tracking-wider text-[#4a4a4a] mb-3 md:mb-4">{t('home.businesses.manpower.label')}</span>
              <div className="text-5xl md:text-[72px] font-extralight text-[#1a1a1a] mb-4 md:mb-6">01</div>
              <h3 className="text-xl md:text-2xl font-semibold text-[#1a1a1a] mb-3 md:mb-4">{t('home.businesses.manpower.title')}</h3>
              <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed flex-grow">
                {t('home.businesses.manpower.description')}
              </p>
            </div>

            {/* AI Layer Card - Elevated */}
            <div className="bg-gradient-to-b from-white to-[#e8f2f7] rounded-lg p-6 md:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.06)] min-h-[360px] md:h-[420px] flex flex-col md:-mt-16">
              <span className="text-xs uppercase tracking-wider text-[#4a4a4a] mb-3 md:mb-4">{t('home.businesses.ai.label')}</span>
              <div className="text-5xl md:text-[72px] font-extralight text-[#1a1a1a] mb-4 md:mb-6">02</div>
              <h3 className="text-xl md:text-2xl font-semibold text-[#1a1a1a] mb-3 md:mb-4">{t('home.businesses.ai.title')}</h3>
              <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed flex-grow">
                {t('home.businesses.ai.description')}
              </p>
            </div>

            {/* BC Robotics Card */}
            <div className="bg-white rounded-lg p-6 md:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.06)] min-h-[360px] md:h-[420px] flex flex-col">
              <span className="text-xs uppercase tracking-wider text-[#4a4a4a] mb-3 md:mb-4">{t('home.businesses.robotics.label')}</span>
              <div className="text-5xl md:text-[72px] font-extralight text-[#1a1a1a] mb-4 md:mb-6">03</div>
              <h3 className="text-xl md:text-2xl font-semibold text-[#1a1a1a] mb-3 md:mb-4">{t('home.businesses.robotics.title')}</h3>
              <p className="text-sm md:text-base text-[#4a4a4a] leading-relaxed flex-grow">
                {t('home.businesses.robotics.description')}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#0a0a0a] py-16 md:py-32 mt-16 md:mt-32">
          <div className="text-center px-4 md:px-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-4 md:mb-6">
              {t('home.cta.headline')}
            </h2>
            <p className="text-base md:text-lg text-gray-400 mb-8 md:mb-12 max-w-2xl mx-auto">
              {t('home.cta.subtext')}
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-3 px-8 md:px-12 py-3 md:py-4 border-2 border-white text-white rounded-full hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              {t('home.cta.button')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
